import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import {
  Trophy,
  ClipboardList,
  CheckCircle,
  X,
  CalendarDays,
  Eye,
  EyeOff,
} from "lucide-react";
import { useGlobal } from "../../context/GlobalContext";
import { images } from "../../assets/images";
import CalendarPicker from "../../components/Calender";
import Pagination from "../../components/Pagination";
import { getLocalDateFromUtc } from "../../utils/timezone";

const emptyPagination = { page: 1, per_page: 12, total: 0, total_pages: 1 };

const Dashboard = () => {
  const { getJobsList, getPointSummary } = useGlobal();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(
    () => sessionStorage.getItem("dashboardTab") || "assigned",
  );

  const handleTabChange = (tab) => {
    sessionStorage.setItem("dashboardTab", tab);
    setActiveTab(tab);
  };
  const [pointSummary, setPointSummary] = useState({});
  const [filterDate, setFilterDate] = useState("");
  const [showCalendar, setShowCalendar] = useState(false);
  const [dropPos, setDropPos] = useState({ top: 0, left: 0 });
  const [hideOverdue, setHideOverdue] = useState(
    () => sessionStorage.getItem("dashboardHideOverdue") === "1",
  );
  const calendarRef = useRef(null);
  const dropRef = useRef(null);

  const [assignedJobs, setAssignedJobs] = useState([]);
  const [assignedPagination, setAssignedPagination] = useState(emptyPagination);
  const [assignedPage, setAssignedPage] = useState(1);

  const [completedJobs, setCompletedJobs] = useState([]);
  const [completedPagination, setCompletedPagination] =
    useState(emptyPagination);
  const [completedPage, setCompletedPage] = useState(1);

  const [assignedTotal, setAssignedTotal] = useState(0);
  const [completedTotal, setCompletedTotal] = useState(0);

  const fetchJobs = async (status) => {
    const { data, pagination } = await getJobsList({ status, page: 1 });
    if (status === "assigned") {
      setAssignedJobs(data);
      setAssignedPagination(pagination);
    } else {
      setCompletedJobs(data);
      setCompletedPagination(pagination);
    }
    if (pagination?.assigned_total !== undefined) {
      setAssignedTotal(pagination.assigned_total);
    }
    if (pagination?.completed_total !== undefined) {
      setCompletedTotal(pagination.completed_total);
    }
  };

  useEffect(() => {
    fetchJobs(activeTab);
  }, [activeTab]);

  useEffect(() => {
    const loadPoints = async () => {
      const points = await getPointSummary();
      setPointSummary(points || {});
    };
    loadPoints();
  }, []);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      const inTrigger = calendarRef.current?.contains(e.target);
      const inDrop = dropRef.current?.contains(e.target);
      if (!inTrigger && !inDrop) setShowCalendar(false);
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  const toggleHideOverdue = () => {
    setHideOverdue((v) => {
      const next = !v;
      sessionStorage.setItem("dashboardHideOverdue", next ? "1" : "0");
      return next;
    });
  };

  const handleCalToggle = () => {
    if (!showCalendar && calendarRef.current) {
      const rect = calendarRef.current.getBoundingClientRect();
      setDropPos({ top: rect.bottom + 6, left: rect.left });
    }
    setShowCalendar((v) => !v);
  };

  const todayLocalDate = (() => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  })();

  const isOverdueJob = (job) => {
    if (job.status?.toLowerCase() === "completed") return false;
    const scheduleLocal = getLocalDateFromUtc(job.schedule_date_time);
    return !!scheduleLocal && scheduleLocal < todayLocalDate;
  };
  const overdueCount = assignedJobs.filter(isOverdueJob).length;

  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return "";
    const [y, m, d] = dateStr.split("-");
    return `${d}/${m}/${y}`;
  };

  const baseJobs = activeTab === "assigned" ? assignedJobs : completedJobs;
  const activePagination =
    activeTab === "assigned" ? assignedPagination : completedPagination;
  const activePage = activeTab === "assigned" ? assignedPage : completedPage;
  const setActivePage =
    activeTab === "assigned" ? setAssignedPage : setCompletedPage;

  const dateFilteredJobs = filterDate
    ? baseJobs.filter(
        (job) => getLocalDateFromUtc(job.schedule_date_time) === filterDate,
      )
    : baseJobs;
  const filteredJobs =
    activeTab === "assigned" && hideOverdue
      ? dateFilteredJobs.filter((job) => !isOverdueJob(job))
      : dateFilteredJobs;
  const PER_PAGE = activePagination.per_page || 12;

  const localTotal = filteredJobs.length;
  const localTotalPages = Math.max(1, Math.ceil(localTotal / PER_PAGE));
  const safeActivePage = Math.min(activePage, localTotalPages);
  const sliceStart = (safeActivePage - 1) * PER_PAGE;
  const pagedJobs = filteredJobs.slice(sliceStart, sliceStart + PER_PAGE);

  const formatScheduledBadge = (utcString) => {
    const local = getLocalDateFromUtc(utcString);
    if (!local) return "";
    const [y, m, d] = local.split("-");
    return `${m}/${d}/${y.slice(-2)}`;
  };

  return (
    <div className="content-part">
      <div className="row g-3 mb-3">
        <div className="col-12 col-md-6">
          <div className="db-stats">
            <div className="db-stat">
              <div className="db-stat-icon db-stat-icon--gold">
                <Trophy size={14} />
              </div>
              <div>
                <div className="db-stat-val">{pointSummary?.balance || 0}</div>
                <div className="db-stat-lbl">Balance</div>
              </div>
            </div>
            <div className="db-stat-divider" />
            <div className="db-stat">
              <div className="db-stat-icon db-stat-icon--blue">
                <ClipboardList size={14} />
              </div>
              <div>
                <div className="db-stat-val">{assignedTotal}</div>
                <div className="db-stat-lbl">Assigned</div>
              </div>
            </div>
            <div className="db-stat-divider" />
            <div className="db-stat">
              <div className="db-stat-icon db-stat-icon--green">
                <CheckCircle size={14} />
              </div>
              <div>
                <div className="db-stat-val">
                  {String(completedTotal).padStart(2, "0")}
                </div>
                <div className="db-stat-lbl">Completed</div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-12 col-md-6">
          <div className="db-goal h-100">
            <div className="db-goal-header">
              <span className="db-goal-label">
                <i className="fa-solid fa-trophy" />
                Reward Summary
              </span>
              <span className="db-goal-pct">
                {pointSummary?.total_redeemed} /{" "}
                {pointSummary?.total_earned || 0} pts
              </span>
            </div>
            {(() => {
              const earned = pointSummary?.total_earned || 0;
              const redeemed = pointSummary?.total_redeemed || 0;

              const progressWidth =
                earned > 0 ? `${(redeemed / earned) * 100}%` : "0%";

              return (
                <>
                  <div className="db-goal-track mt-3">
                    <div
                      className="db-goal-fill"
                      style={{ width: progressWidth }}
                    />
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      </div>

      {/* Tabs + Date Filter */}
      <div className="row g-2 align-items-center mb-3">
        {/* Tabs — col-8 */}
        <div className="col-12 col-md-8 order-md-1">
          <div className="db-tabs">
            <button
              className={`db-tab ${activeTab === "assigned" ? "active" : ""}`}
              onClick={() => handleTabChange("assigned")}
            >
              <ClipboardList size={14} />
              Assigned
              <span className="db-tab-count">{assignedTotal}</span>
            </button>
            <button
              className={`db-tab ${activeTab === "completed" ? "active" : ""}`}
              onClick={() => handleTabChange("completed")}
            >
              <CheckCircle size={14} />
              Completed
              <span className="db-tab-count">{completedTotal}</span>
            </button>
          </div>
        </div>

        {/* Overdue toggle — second row on mobile (left), own row on desktop (right) */}
        {activeTab === "assigned" && overdueCount > 0 && (
          <div className="col-auto col-md-12 order-md-3 d-flex justify-content-end">
            <button
              type="button"
              className={`db-overdue-toggle${hideOverdue ? " is-active" : ""}`}
              onClick={toggleHideOverdue}
            >
              {hideOverdue ? <Eye size={13} /> : <EyeOff size={13} />}
              <span>
                {hideOverdue ? "Show overdue" : "Hide overdue"} ({overdueCount})
              </span>
            </button>
          </div>
        )}

        {/* Date Filter — col-4 */}
        <div className="col col-md-4 order-md-2" ref={calendarRef}>
          <div
            className={`db-cal-wrap${filterDate ? " has-value" : ""}`}
            onClick={handleCalToggle}
          >
            <CalendarDays size={14} className="db-cal-icon" />
            <span className="db-cal-label">
              {filterDate ? formatDisplayDate(filterDate) : "Pick date"}
            </span>
            {filterDate && (
              <button
                className="db-cal-clear"
                onClick={(e) => {
                  e.stopPropagation();
                  setFilterDate("");
                  setShowCalendar(false);
                }}
                aria-label="Clear date filter"
              >
                <X size={11} />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Job Cards */}
      <div className="row g-3">
        {filteredJobs.length === 0 ? (
          <div className="col-12">
            <div className="db-empty-state">
              <div className="db-empty-icon">
                {activeTab === "assigned" ? (
                  <ClipboardList size={40} strokeWidth={1.5} />
                ) : (
                  <CheckCircle size={40} strokeWidth={1.5} />
                )}
              </div>
              <h6 className="db-empty-title">
                {filterDate
                  ? `No jobs ${activeTab} on ${formatDisplayDate(filterDate)}`
                  : activeTab === "assigned"
                    ? "No Assigned Jobs"
                    : "No Completed Jobs"}
              </h6>
              <p className="db-empty-desc">
                {filterDate
                  ? "There are no jobs scheduled for this date. Try a different date or clear the filter."
                  : activeTab === "assigned"
                    ? "You don't have any assigned jobs at the moment. Check back later."
                    : "You haven't completed any jobs yet. Completed jobs will appear here."}
              </p>
            </div>
          </div>
        ) : (
          pagedJobs.map((item) => (
            <div key={item.job_pid} className="col-12 col-sm-6 col-xl-4">
              <div
                className="db-card"
                onClick={() =>
                  navigate("/details", {
                    state: { job_pid: item.job_pid, name: item.services },
                  })
                }
                role="button"
                tabIndex={0}
                onKeyDown={(e) =>
                  e.key === "Enter" &&
                  navigate("/details", {
                    state: { job_pid: item.job_pid, name: item.services },
                  })
                }
              >
                <div className="db-card-img">
                  <img
                    src={item.image || images.placeHolderImg}
                    alt={item.services}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = images.placeHolderImg;
                    }}
                  />
                  {item.price != null && (
                    <span className="db-card-price-badge">
                      ${Number(item.price).toLocaleString()}
                    </span>
                  )}
                  {(() => {
                    const scheduleLocal = getLocalDateFromUtc(
                      item.schedule_date_time,
                    );
                    if (!scheduleLocal) return null;
                    if (activeTab === "completed") {
                      return (
                        <span className="db-card-sched-badge db-card-sched-badge--completed">
                          <i className="fa-solid fa-circle-check" />
                          Completed on {formatScheduledBadge(item.completed_at)}
                        </span>
                      );
                    }
                    if (item.status === "In Progress") {
                      return (
                        <span className="db-card-sched-badge db-card-sched-badge--progress">
                          <i className="fa-solid fa-spinner" />
                          In Progress
                        </span>
                      );
                    }
                    const isToday = scheduleLocal === todayLocalDate;
                    const isFuture = scheduleLocal > todayLocalDate;
                    const isOverdue = scheduleLocal < todayLocalDate;
                    if (isOverdue) {
                      return (
                        <span className="db-card-sched-badge db-card-sched-badge--overdue">
                          <i className="fa-solid fa-triangle-exclamation" />
                          Overdue ·{" "}
                          {formatScheduledBadge(item.schedule_date_time)}
                        </span>
                      );
                    }
                    if (!isToday && !isFuture) return null;
                    return (
                      <span className="db-card-sched-badge">
                        <i className="fa-regular fa-calendar" />
                        Scheduled on{" "}
                        {isToday
                          ? "Today"
                          : formatScheduledBadge(item.schedule_date_time)}
                      </span>
                    );
                  })()}
                </div>
                <div className="db-card-body">
                  {!item.is_primary && (
                    <span className="db-card-helper-note">
                      <i className="fa-solid fa-user-group" />
                      You are assigned as a rookie
                    </span>
                  )}
                  <h6 className="db-card-title line-clamp-2">
                    {item.services}
                  </h6>
                  <p className="db-card-desc">{item.address}</p>
                  <div className="db-card-footer">
                    <div className="db-card-loc">
                      <i className="fa-solid fa-location-dot" />
                      <span>{item.location}</span>
                    </div>
                    <div className="db-card-arrow">
                      <i className="fa-solid fa-arrow-right" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {localTotalPages > 1 && (
        <div className="db-pagination-wrap">
          <Pagination
            currentPage={safeActivePage}
            totalPages={localTotalPages}
            perPage={PER_PAGE}
            total={localTotal}
            onPageChange={(p) => setActivePage(p)}
          />
        </div>
      )}

      {/* Calendar portal — renders on body to escape overflow:auto containers */}
      {showCalendar &&
        createPortal(
          <div
            ref={dropRef}
            className="db-cal-drop"
            style={{ top: dropPos.top, left: dropPos.left }}
          >
            <CalendarPicker
              mode="default"
              value={filterDate ? filterDate.replace(/-/g, "/") : undefined}
              onDateChange={(dateObj) => {
                const yyyy = dateObj.getFullYear();
                const mm = String(dateObj.getMonth() + 1).padStart(2, "0");
                const dd = String(dateObj.getDate()).padStart(2, "0");
                setFilterDate(`${yyyy}-${mm}-${dd}`);
                setShowCalendar(false);
              }}
            />
          </div>,
          document.body,
        )}
    </div>
  );
};

export default Dashboard;
